// OneOnOneMode.tsx — Canvas Manager Audit · 1-on-1 Mode (Ships 2+3, May 13, 2026)
//
// Self-contained pane for the manager-driven weekly 1-on-1 ritual.
//   • Left rail: canvasable team members with match% chips + flagged badge.
//   • Main: selected rep header (with last-1:1 date), Recap card (7d / 30d
//     toggle), commitment editor (stub for Ship 4), per-rep audit detail
//     accordion (shifts + coachable flags), "Mark 1:1 ✓" → oneone_meetings.
//   • 30-day view: aggregate stats + 4 collapsible weekly rows + auto-detected
//     pattern callouts (recurring slot misses · trend direction · strongest /
//     weakest slots) computed by commitmentMath helpers.
//
// Ship 4: full commitment editor + lock workflow.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../context/AuthContext";
import MondayGoalsCard from "./MondayGoalsCard";
import CommissionGoalChart from "../../snapshot/CommissionGoalChart";
import {
  type CommitmentMatchRow,
  type DayLabel,
  type RecurringMiss,
  type SlotPerformance,
  type WeeklyStat,
  DAY_LABELS,
  BLOCK_LABELS,
  aggregateMatch,
  mondayOf,
  stateClasses,
  weekRangeLabel,
  groupByWeek,
  weeklyStats,
  detectRecurringMisses,
  detectTrend,
  slotPerformance,
  slotLabel,
} from "../../../lib/commitmentMath";

const CANVASS_ROLES = new Set([
  "restore_manager",
  "team_lead",
  "asm",
  "canvasser",
]);

const ROLE_BADGE: Record<string, { label: string; cls: string }> = {
  asm:             { label: "ASM",  cls: "bg-amber-100 text-amber-800" },
  team_lead:       { label: "TL",   cls: "bg-blue-100 text-blue-800" },
  restore_manager: { label: "RM",   cls: "bg-purple-100 text-purple-800" },
  canvasser:       { label: "CNV",  cls: "bg-teal-100 text-teal-800" },
};

interface RailMember {
  id: string;
  full_name: string;
  role: string;
  team_lead_id: string | null;
  asm_id: string | null;
  commitment_streak_weeks: number;
  commitment_last_match_pct: number | null;
}

interface OneOneMeeting {
  id: string;
  user_id: string;
  manager_id: string;
  week_start: string;
  notes: string | null;
  completed_at: string;
}

interface ShiftRow {
  shift_id: string;
  user_id: string;
  full_name: string;
  role: string;
  started_at: string;
  ended_at: string | null;
  hours_worked: number;
  knocks: number;
  contacts: number;
  appts: number;
  claims: number;
  miles_walked: number | null;
  pings: number;
  contact_rate: number;
  appt_rate: number;
  has_low_contact: boolean;
  has_low_appt: boolean;
  has_short_session: boolean;
  has_few_pings: boolean;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function avatarBg(id: string): string {
  const palette = [
    "#7c3aed", "#0ea5e9", "#16a34a", "#dc2626", "#0891b2",
    "#be185d", "#a16207", "#65a30d", "#475569", "#0f766e",
    "#7c2d12", "#9333ea", "#0369a1", "#15803d", "#b45309",
  ];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length]!;
}

function matchTone(pct: number | null): string {
  if (pct == null) return "bg-slate-100 text-slate-500";
  if (pct >= 80)   return "bg-emerald-100 text-emerald-700";
  if (pct >= 60)   return "bg-amber-100 text-amber-800";
  return "bg-red-100 text-red-800";
}

function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const ms = today.getTime() - d.getTime();
  const days = Math.floor(ms / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

type RailFilter = "all" | "flagged" | "needs_11";
type RecapRange = "7d" | "30d";

export default function OneOnOneMode() {
  const { member: currentUser } = useAuth();
  const [members, setMembers] = useState<RailMember[]>([]);
  const [memErr, setMemErr] = useState<string | null>(null);
  const [memLoading, setMemLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<RailFilter>("all");

  const [recapRange, setRecapRange] = useState<RecapRange>("7d");

  const lastWeekStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return mondayOf(d);
  }, []);
  const thisWeekStart = useMemo(() => mondayOf(new Date()), []);
  // 30d window = last 4 completed weeks (most recent first via reverse range)
  const range30StartDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 28);
    return mondayOf(d);
  }, []);

  // Live data
  const [recapRows, setRecapRows] = useState<CommitmentMatchRow[]>([]);   // last week
  const [recap30Rows, setRecap30Rows] = useState<CommitmentMatchRow[]>([]); // last 4 weeks
  const [recapLoading, setRecapLoading] = useState(false);
  // Realtime refresh trigger. Increments on any relevant table change for the
  // selected rep; the data-load useEffect depends on it. Replaces the earlier
  // setSelectedId((id) => id) pattern which was a no-op (React bails out when
  // state value is unchanged, so the effect never refired).
  const [refreshTick, setRefreshTick] = useState(0);
  const [meetings, setMeetings] = useState<OneOneMeeting[]>([]);
  const [shifts, setShifts] = useState<ShiftRow[]>([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);


  // Mark 1:1 ✓ flow
  const [oneOneNotes, setOneOneNotes] = useState("");
  const [oneOneSaving, setOneOneSaving] = useState(false);
  const [oneOneErr, setOneOneErr] = useState<string | null>(null);
  const [oneOneJustSaved, setOneOneJustSaved] = useState(false);

  // Roster
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setMemLoading(true);
      const { data, error } = await supabase
        .from("team_roster")
        .select(
          "id, full_name, role, team_lead_id, asm_id, commitment_streak_weeks, commitment_last_match_pct"
        )
        .eq("is_active", true)
        .eq("on_leave", false)
        .order("full_name");
      if (cancelled) return;
      if (error) {
        setMemErr(error.message);
        setMembers([]);
      } else {
        const filtered = (data ?? []).filter((m: { role: string }) => CANVASS_ROLES.has(m.role));
        setMembers(filtered as RailMember[]);
        if (filtered.length > 0 && !selectedId) setSelectedId(filtered[0]!.id);
      }
      setMemLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load all per-rep data on rep change
  useEffect(() => {
    if (!selectedId) { setRecapRows([]); setRecap30Rows([]); setMeetings([]); setShifts([]); return; }
    let cancelled = false;
    (async () => {
      setRecapLoading(true);
      setShiftsLoading(true);
      const rangeStart = new Date(); rangeStart.setDate(rangeStart.getDate() - 30);
      const rangeEnd = new Date();
      const [last, thirty, mts, shiftsRes] = await Promise.all([
        supabase
          .from("weekly_commitment_match")
          .select("commitment_id,user_id,week_start,day_label,block_label,day_offset,committed,has_shift,block_starts_at,block_ends_at,state")
          .eq("user_id", selectedId)
          .eq("week_start", lastWeekStart),
        supabase
          .from("weekly_commitment_match")
          .select("commitment_id,user_id,week_start,day_label,block_label,day_offset,committed,has_shift,block_starts_at,block_ends_at,state")
          .eq("user_id", selectedId)
          .gte("week_start", range30StartDate)
          .lt("week_start", thisWeekStart),
        supabase
          .from("oneone_meetings")
          .select("id,user_id,manager_id,week_start,notes,completed_at")
          .eq("user_id", selectedId)
          .order("completed_at", { ascending: false })
          .limit(8),
        supabase.rpc("get_canvas_audit_shifts", {
          p_start: rangeStart.toISOString(),
          p_end: rangeEnd.toISOString(),
          p_user: selectedId,
        }),
      ]);
      if (cancelled) return;
      setRecapRows((last.data ?? []) as CommitmentMatchRow[]);
      setRecap30Rows((thirty.data ?? []) as CommitmentMatchRow[]);
      setMeetings((mts.data ?? []) as OneOneMeeting[]);
      setShifts(((shiftsRes.data ?? []) as ShiftRow[]));
      setRecapLoading(false);
      setShiftsLoading(false);
      setOneOneNotes(""); setOneOneErr(null); setOneOneJustSaved(false);
    })();
    return () => { cancelled = true; };
  }, [selectedId, lastWeekStart, thisWeekStart, range30StartDate, refreshTick]);

  // Realtime: refetch this rep's recap when relevant tables change for them
  useEffect(() => {
    if (!selectedId) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const bump = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setRefreshTick((n) => n + 1), 800);
    };
    const ch = supabase
      .channel(`canvas-1on1-${selectedId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "weekly_commitments",  filter: `user_id=eq.${selectedId}` }, bump)
      .on("postgres_changes", { event: "*", schema: "public", table: "canvas_shifts",       filter: `user_id=eq.${selectedId}` }, bump)
      .on("postgres_changes", { event: "*", schema: "public", table: "oneone_meetings",     filter: `user_id=eq.${selectedId}` }, bump)
      .subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      supabase.removeChannel(ch);
    };
  }, [selectedId]);


  const selectedMember = useMemo(
    () => members.find((m) => m.id === selectedId) ?? null,
    [members, selectedId]
  );

  const visibleMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((m) => {
      if (q && !m.full_name.toLowerCase().includes(q)) return false;
      if (filter === "flagged" && (m.commitment_last_match_pct == null || m.commitment_last_match_pct >= 80)) return false;
      if (filter === "needs_11" && (m.commitment_last_match_pct == null || m.commitment_last_match_pct >= 60)) return false;
      return true;
    });
  }, [members, search, filter]);

  const flaggedCount = useMemo(
    () => members.filter((m) => m.commitment_last_match_pct != null && m.commitment_last_match_pct < 80).length,
    [members]
  );
  const needs11Count = useMemo(
    () => members.filter((m) => m.commitment_last_match_pct != null && m.commitment_last_match_pct < 60).length,
    [members]
  );

  const recap7Stats   = useMemo(() => aggregateMatch(recapRows),   [recapRows]);
  const recap30Stats  = useMemo(() => aggregateMatch(recap30Rows), [recap30Rows]);
  const recap30ByWeek = useMemo(() => groupByWeek(recap30Rows),    [recap30Rows]);
  const weeklies      = useMemo(() => weeklyStats(recap30ByWeek),  [recap30ByWeek]);
  const recurringMiss = useMemo(() => detectRecurringMisses(recap30ByWeek, 3), [recap30ByWeek]);
  const trend         = useMemo(() => detectTrend(weeklies),       [weeklies]);
  const slotPerf      = useMemo(() => slotPerformance(recap30ByWeek), [recap30ByWeek]);
  const lastMeeting   = meetings[0] ?? null;

  async function markOneOneComplete() {
    if (!selectedId || !currentUser) return;
    setOneOneSaving(true);
    setOneOneErr(null);
    const { error } = await supabase.from("oneone_meetings").insert({
      user_id: selectedId,
      manager_id: currentUser.id,
      week_start: thisWeekStart,
      notes: oneOneNotes.trim() ? oneOneNotes.trim() : null,
    });
    setOneOneSaving(false);
    if (error) {
      setOneOneErr(error.message);
    } else {
      setOneOneJustSaved(true);
      setOneOneNotes("");
      // Realtime sub will refetch meetings; reset the flash after 2.5s
      setTimeout(() => setOneOneJustSaved(false), 2500);
    }
  }


  // ────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
    <div className="grid grid-cols-12 gap-3">
      {/* LEFT RAIL */}
      <aside className="col-span-12 md:col-span-3">
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 p-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reps…"
              className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-orange-500"
            />
            <div className="mt-2 flex gap-1 text-[11px] font-bold">
              <FilterPill active={filter === "all"}       onClick={() => setFilter("all")}>All · {members.length}</FilterPill>
              <FilterPill active={filter === "flagged"}   onClick={() => setFilter("flagged")}>⚠ {flaggedCount}</FilterPill>
              <FilterPill active={filter === "needs_11"}  onClick={() => setFilter("needs_11")}>📅 {needs11Count}</FilterPill>
            </div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {memLoading ? (
              <div className="p-4 text-xs text-slate-500">Loading roster…</div>
            ) : memErr ? (
              <div className="p-3 text-xs text-red-700">{memErr}</div>
            ) : visibleMembers.length === 0 ? (
              <div className="p-4 text-xs text-slate-500">No reps match.</div>
            ) : (
              visibleMembers.map((m) => {
                const selected = m.id === selectedId;
                const flagged = m.commitment_last_match_pct != null && m.commitment_last_match_pct < 80;
                const role = ROLE_BADGE[m.role] ?? { label: m.role.slice(0, 3).toUpperCase(), cls: "bg-slate-100 text-slate-700" };
                return (
                  <button
                    key={m.id}
                    onClick={() => setSelectedId(m.id)}
                    className={`flex w-full items-center gap-2 px-3 py-2.5 text-left transition ${
                      selected
                        ? "border-l-[3px] border-orange-500 bg-orange-50/40"
                        : "border-l-[3px] border-transparent hover:bg-slate-50"
                    }`}
                  >
                    <div
                      className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-extrabold text-white"
                      style={{ background: avatarBg(m.id) }}
                    >
                      {initialsOf(m.full_name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 truncate text-[13px] font-bold text-slate-900">
                        <span className="truncate">{m.full_name}</span>
                        {flagged ? <span className="rounded bg-red-100 px-1 py-0.5 text-[9px] font-bold text-red-800">⚠</span> : null}
                      </div>
                      <div className="flex items-center gap-1 truncate text-[10px] text-slate-500">
                        <span className={`rounded px-1.5 py-0.5 font-bold ${role.cls}`}>{role.label}</span>
                        {m.commitment_streak_weeks > 0 ? <span>· 🔥{m.commitment_streak_weeks}</span> : null}
                      </div>
                    </div>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${matchTone(m.commitment_last_match_pct)}`}>
                      {m.commitment_last_match_pct != null ? `${Math.round(m.commitment_last_match_pct)}%` : "—"}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </aside>

      {/* MAIN */}
      <main className="col-span-12 space-y-3 md:col-span-9">
        {selectedMember ? (
          <>
            {/* Selected rep header */}
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
              <div
                className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-base font-extrabold text-white"
                style={{ background: avatarBg(selectedMember.id) }}
              >
                {initialsOf(selectedMember.full_name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="truncate text-base font-bold text-slate-900">{selectedMember.full_name}</h2>
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                      (ROLE_BADGE[selectedMember.role] ?? { cls: "bg-slate-100 text-slate-700" }).cls
                    }`}
                  >
                    {(ROLE_BADGE[selectedMember.role] ?? { label: selectedMember.role }).label}
                  </span>
                  {selectedMember.commitment_streak_weeks > 0 ? (
                    <span className="text-[11px] font-bold text-orange-700">🔥 {selectedMember.commitment_streak_weeks}-wk streak</span>
                  ) : null}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                  <span>
                    Last completed match:{" "}
                    <strong className="text-slate-700">
                      {selectedMember.commitment_last_match_pct != null
                        ? `${Math.round(selectedMember.commitment_last_match_pct)}%`
                        : "—"}
                    </strong>
                  </span>
                  {lastMeeting ? (
                    <span>· Last 1:1 <strong className="text-slate-700">{formatRelativeDate(lastMeeting.completed_at)}</strong></span>
                  ) : (
                    <span className="text-amber-700">· No 1:1 logged yet</span>
                  )}
                </div>
              </div>
            </div>

            {/* Week goals + the month's build goal (Oct 2, 2026). The weekly
                AM/PM/EVE grid retired — days are confirmed Tue–Fri on the
                ✅ Today board. */}
            <MondayGoalsCard
              userId={selectedMember.id}
              role={selectedMember.role}
              managerId={currentUser?.id ?? null}
            />

            {/* Old weekly AM/PM/EVE plans (retired Oct 2, 2026) — kept for history */}
            <details className="rounded-xl border border-slate-200 bg-white">
              <summary className="cursor-pointer list-none p-3 text-xs font-semibold text-slate-500 hover:bg-slate-50">
                📅 Old weekly plans (through Sep 2026)
              </summary>
              <div className="border-t border-slate-200 p-3">
            <RecapCard
              range={recapRange}
              setRange={setRecapRange}
              loading={recapLoading}
              rows7d={recapRows}
              rows30d={recap30Rows}
              weekStart7d={lastWeekStart}
              weekStart30dStart={range30StartDate}
              stats7d={recap7Stats}
              stats30d={recap30Stats}
              weeklies={weeklies}
              recurringMiss={recurringMiss}
              trend={trend}
              slotPerf={slotPerf}
            />
              </div>
            </details>

            {/* Commission goal for the year (Sep 28, 2026) — picked here at the
                1-on-1, the rep sees the same chart on their home page. */}
            {selectedMember.role !== "canvasser" && (
              <CommissionGoalChart
                memberId={selectedMember.id}
                editable
                managerId={currentUser?.id ?? null}
              />
            )}


            {/* Audit detail accordion (Ship 3 — now live) */}
            <details className="rounded-xl border border-slate-200 bg-white" open>
              <summary className="cursor-pointer list-none p-3 text-sm font-bold text-slate-900 hover:bg-slate-50">
                🔍 Audit detail
                <span className="ml-2 text-[11px] font-normal text-slate-500">
                  Last 30d · {shifts.length} shift{shifts.length === 1 ? "" : "s"}
                </span>
              </summary>
              <div className="border-t border-slate-200 p-3 space-y-3">
                <AuditDetailContent loading={shiftsLoading} shifts={shifts} />
              </div>
            </details>

            {/* Mark 1:1 ✓ panel */}
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex items-center gap-2">
                <div className="text-sm font-bold text-slate-900">🎯 Mark 1-on-1 complete</div>
                <span className="text-[11px] text-slate-500">Logs to <code className="rounded bg-slate-100 px-1">oneone_meetings</code></span>
              </div>
              <textarea
                value={oneOneNotes}
                onChange={(e) => setOneOneNotes(e.target.value)}
                placeholder="Notes from today's 1:1 (optional) — what you agreed on, what to revisit next week…"
                rows={2}
                className="mt-2 w-full resize-none rounded-md border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-orange-500"
              />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  onClick={markOneOneComplete}
                  disabled={oneOneSaving || !currentUser}
                  className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-50 hover:bg-orange-600"
                >
                  {oneOneSaving ? "Saving…" : "Mark 1-on-1 complete ✓"}
                </button>
                {oneOneJustSaved ? <span className="text-[11px] font-bold text-emerald-700">Saved.</span> : null}
                {oneOneErr ? <span className="text-[11px] text-red-700">{oneOneErr}</span> : null}
                {!currentUser ? <span className="text-[11px] text-amber-700">Auth required.</span> : null}
                {meetings.length > 0 ? (
                  <span className="ml-auto text-[11px] text-slate-500">
                    {meetings.length} previous 1:1{meetings.length === 1 ? "" : "s"} on file
                  </span>
                ) : null}
              </div>
              {meetings.length > 0 ? (
                <div className="mt-2 border-t border-slate-100 pt-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Recent 1:1s</div>
                  <ul className="mt-1 space-y-1 text-[11px] text-slate-600">
                    {meetings.slice(0, 4).map((m) => (
                      <li key={m.id}>
                        <strong className="text-slate-800">{new Date(m.completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</strong>
                        {" "}— wk of {new Date(m.week_start + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        {m.notes ? <> · <em className="text-slate-500">{m.notes}</em></> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            Pick a rep on the left to start.
          </div>
        )}
      </main>
    </div>
    </div>
  );
}

function FilterPill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-md px-2 py-1 ${active ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-100"}`}
    >
      {children}
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────
// RecapCard — 7d or 30d view with [Last week | Past 30 days] toggle.
// 30d adds: 5-tile aggregate stats, 4 collapsible weekly rows, pattern callouts.
// ────────────────────────────────────────────────────────────────────────
function RecapCard({
  range, setRange,
  loading,
  rows7d, rows30d,
  weekStart7d, weekStart30dStart,
  stats7d, stats30d,
  weeklies,
  recurringMiss, trend, slotPerf,
}: {
  range: RecapRange;
  setRange: (r: RecapRange) => void;
  loading: boolean;
  rows7d: CommitmentMatchRow[];
  rows30d: CommitmentMatchRow[];
  weekStart7d: string;
  weekStart30dStart: string;
  stats7d: ReturnType<typeof aggregateMatch>;
  stats30d: ReturnType<typeof aggregateMatch>;
  weeklies: WeeklyStat[];
  recurringMiss: RecurringMiss[];
  trend: ReturnType<typeof detectTrend>;
  slotPerf: SlotPerformance[];
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-bold text-slate-900">📅 Recap</h3>
          <div className="inline-flex rounded-md bg-slate-100 p-0.5">
            <button
              onClick={() => setRange("7d")}
              className={`rounded-sm px-2.5 py-1 text-[11px] font-bold transition ${range === "7d" ? "bg-slate-900 text-white" : "text-slate-600"}`}
            >
              Last week
            </button>
            <button
              onClick={() => setRange("30d")}
              className={`rounded-sm px-2.5 py-1 text-[11px] font-bold transition ${range === "30d" ? "bg-slate-900 text-white" : "text-slate-600"}`}
            >
              Past 30 days
            </button>
          </div>
          <span className="text-[11px] text-slate-500">
            {range === "7d" ? weekRangeLabel(weekStart7d) : `${weekRangeLabel(weekStart30dStart)} → ${weekRangeLabel(weekStart7d)}`}
          </span>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-600">
          <Legend swatch="bg-orange-500" label="committed" />
          <Legend swatch="bg-emerald-500" label="showed" />
          <Legend swatch="bg-slate-300" label="missed" />
          <Legend swatch="bg-amber-100 border border-amber-500 border-dashed" label="orphan" />
        </div>
      </div>

      {loading ? (
        <div className="py-6 text-center text-xs text-slate-500">Loading recap…</div>
      ) : range === "7d" ? (
        <WeekGridView rows={rows7d} weekStart={weekStart7d} stats={stats7d} />
      ) : (
        <ThirtyDayView
          rows={rows30d}
          stats={stats30d}
          weeklies={weeklies}
          recurringMiss={recurringMiss}
          trend={trend}
          slotPerf={slotPerf}
        />
      )}
    </div>
  );
}

function WeekGridView({
  rows, weekStart, stats,
}: {
  rows: CommitmentMatchRow[];
  weekStart: string;
  stats: ReturnType<typeof aggregateMatch>;
}) {
  const grid = useMemo(() => {
    const m = new Map<string, CommitmentMatchRow>();
    for (const r of rows) m.set(`${r.day_label}|${r.block_label}`, r);
    return m;
  }, [rows]);

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
        No commitment locked for {weekRangeLabel(weekStart)} yet.
      </div>
    );
  }

  const dayHeader = (d: DayLabel) => d.charAt(0).toUpperCase() + d.slice(1);

  return (
    <>
      <table className="w-full table-fixed text-center text-[11px]">
        <thead>
          <tr className="font-bold uppercase tracking-wide text-slate-500">
            <th className="w-12 pl-1 text-left"></th>
            {DAY_LABELS.map((d) => <th key={d} className="py-1.5">{dayHeader(d)}</th>)}
          </tr>
        </thead>
        <tbody>
          {BLOCK_LABELS.map((b) => (
            <tr key={b}>
              <td className="pl-1 text-left text-[10px] font-bold uppercase text-slate-600">
                {b === "eve" ? "EVE" : b.toUpperCase()}
              </td>
              {DAY_LABELS.map((d) => {
                const row = grid.get(`${d}|${b}`);
                return (
                  <td key={d} className="py-1">
                    <Cell row={row} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 grid grid-cols-4 gap-2 text-center">
        <StatTile label="Committed" value={stats.committed} />
        <StatTile label="Showed" value={stats.showed} accent="emerald" />
        <StatTile label="No-shows" value={stats.missed} accent={stats.missed > 0 ? "red" : undefined} />
        <StatTile label="Match" value={stats.matchPct != null ? `${stats.matchPct}%` : "—"} />
      </div>
    </>
  );
}

function ThirtyDayView({
  rows, stats, weeklies, recurringMiss, trend, slotPerf,
}: {
  rows: CommitmentMatchRow[];
  stats: ReturnType<typeof aggregateMatch>;
  weeklies: WeeklyStat[];
  recurringMiss: RecurringMiss[];
  trend: ReturnType<typeof detectTrend>;
  slotPerf: SlotPerformance[];
}) {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
        No completed-week commitment data in the past 30 days yet.
      </div>
    );
  }

  const strongest = slotPerf.filter((s) => s.committed >= 2).slice(0, 3);
  const weakest = [...slotPerf].filter((s) => s.committed >= 2).pop() ?? null;

  return (
    <>
      {/* Aggregate strip */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <StatTile label="Committed" value={stats.committed} />
        <StatTile label="Showed" value={stats.showed} accent="emerald" />
        <StatTile label="No-shows" value={stats.missed} accent={stats.missed > 0 ? "red" : undefined} />
        <StatTile label="Orphan shifts" value={stats.orphan} accent={stats.orphan > 0 ? "amber" : undefined} />
        <StatTile
          label="Match avg"
          value={stats.matchPct != null ? `${stats.matchPct}%` : "—"}
          accent={stats.matchPct != null && stats.matchPct >= 80 ? "emerald" : stats.matchPct != null && stats.matchPct < 60 ? "red" : undefined}
        />
      </div>

      {/* Pattern callouts */}
      {(recurringMiss.length > 0 || trend.direction !== "flat" && trend.direction !== "insufficient_data" || strongest.length > 0) ? (
        <div className="mt-4 space-y-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Patterns over the window</div>
          {recurringMiss.slice(0, 3).map((p) => (
            <Callout key={`${p.day}|${p.block}`} tone="amber">
              <strong>⚠ {slotLabel({ day: p.day, block: p.block })} keeps slipping</strong>
              {" · "}{p.weeks_missed} of {p.weeks_with_data} weeks{" "}
              <span className="text-slate-500">
                ({p.missed_week_starts.map((ws) => new Date(ws + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })).join(" · ")})
              </span>
            </Callout>
          ))}
          {trend.direction === "up" ? (
            <Callout tone="emerald">
              <strong>📈 Trending up</strong>{" · match rate "}
              {trend.oldest_pct}% → {trend.newest_pct}% (+{trend.diff} pts over {weeklies.length} weeks). Reinforce what's working.
            </Callout>
          ) : null}
          {trend.direction === "down" ? (
            <Callout tone="red">
              <strong>📉 Trending down</strong>{" · match rate "}
              {trend.oldest_pct}% → {trend.newest_pct}% ({trend.diff} pts over {weeklies.length} weeks). Worth digging in.
            </Callout>
          ) : null}
          {strongest.length > 0 ? (
            <Callout tone="slate">
              <strong>📊 Strongest slots ·</strong>{" "}
              {strongest.map((s) => `${slotLabel(s)} (${s.show_rate}%)`).join(" · ")}
              {weakest && weakest.show_rate < (strongest[0]?.show_rate ?? 100) ? (
                <> · <span className="text-amber-700">Weakest: {slotLabel(weakest)} ({weakest.show_rate}%)</span></>
              ) : null}
            </Callout>
          ) : null}
        </div>
      ) : null}

      {/* Weekly rows */}
      <div className="mt-4 space-y-1.5">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Week by week · click to expand the daily grid</div>
        {weeklies.length === 0 ? (
          <div className="text-xs text-slate-500">No completed weeks yet.</div>
        ) : (
          weeklies.map((w) => <WeekRow key={w.week_start} stat={w} rows={rows.filter((r) => r.week_start === w.week_start)} />)
        )}
      </div>
    </>
  );
}

function WeekRow({ stat, rows }: { stat: WeeklyStat; rows: CommitmentMatchRow[] }) {
  const tone =
    stat.match_pct == null ? "bg-slate-100 text-slate-600"
    : stat.match_pct >= 80 ? "bg-emerald-100 text-emerald-700"
    : stat.match_pct >= 60 ? "bg-amber-100 text-amber-800"
    : "bg-red-100 text-red-800";

  return (
    <details className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <summary className="flex cursor-pointer flex-wrap items-center gap-3 p-2 hover:bg-slate-50">
        <span className="text-[10px] text-slate-400">▶</span>
        <div className="min-w-[7rem] text-[12px] font-bold text-slate-700">
          Wk of {new Date(stat.week_start + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
        </div>
        <div className="flex-1 text-[11px] text-slate-600">
          <span>{stat.committed} committed</span>
          {" · "}<span className="text-emerald-700 font-bold">{stat.showed} showed</span>
          {stat.missed > 0 ? <> · <span className="text-red-700">{stat.missed} no-show{stat.missed === 1 ? "" : "s"}</span></> : null}
          {stat.orphan > 0 ? <> · <span className="text-amber-700">{stat.orphan} orphan</span></> : null}
        </div>
        <div className="flex items-center gap-1.5">
          {stat.committed > 0 ? (
            <>
              <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-200"><div className="h-full bg-emerald-500" style={{ width: `${stat.match_pct ?? 0}%` }} /></div>
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${tone}`}>
                {stat.match_pct != null ? `${stat.match_pct}%` : "—"}
              </span>
            </>
          ) : <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">no plan</span>}
        </div>
      </summary>
      <div className="border-t border-slate-200 bg-slate-50/40 p-3">
        <WeekGridDaily rows={rows} />
      </div>
    </details>
  );
}

function WeekGridDaily({ rows }: { rows: CommitmentMatchRow[] }) {
  const grid = useMemo(() => {
    const m = new Map<string, CommitmentMatchRow>();
    for (const r of rows) m.set(`${r.day_label}|${r.block_label}`, r);
    return m;
  }, [rows]);

  return (
    <table className="w-full table-fixed text-center text-[10px]">
      <thead>
        <tr className="font-bold uppercase tracking-wide text-slate-500">
          <th className="w-8 pl-1 text-left"></th>
          {DAY_LABELS.map((d) => <th key={d} className="py-1">{d.charAt(0).toUpperCase() + d.slice(1)}</th>)}
        </tr>
      </thead>
      <tbody>
        {BLOCK_LABELS.map((b) => (
          <tr key={b}>
            <td className="pl-1 text-left text-[9px] font-bold uppercase text-slate-600">
              {b === "eve" ? "EVE" : b.toUpperCase()}
            </td>
            {DAY_LABELS.map((d) => <td key={d} className="py-0.5"><Cell row={grid.get(`${d}|${b}`)} /></td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Cell({ row }: { row: CommitmentMatchRow | undefined }) {
  if (!row) return <span className="inline-block h-5 w-7 rounded border border-dashed border-slate-200 text-[9px] leading-5 text-slate-300">—</span>;
  const c = stateClasses(row.state);
  const labelByState: Record<string, string> = {
    committed_showed: "✓",
    committed_missed: "✕",
    committed_future: "·",
    orphan_shift: "?",
    off_planned: "—",
  };
  return (
    <span
      title={`${row.day_label} ${row.block_label} · ${row.state}`}
      className={`inline-flex h-5 w-7 items-center justify-center rounded border text-[10px] font-bold ${c.bg} ${c.border} ${c.text}`}
    >
      {labelByState[row.state]}
    </span>
  );
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block h-2.5 w-2.5 rounded-sm ${swatch}`} />
      <span>{label}</span>
    </span>
  );
}

function StatTile({ label, value, accent }: { label: string; value: number | string; accent?: "emerald" | "red" | "amber" }) {
  const valueClass =
    accent === "emerald" ? "text-emerald-700"
      : accent === "red" ? "text-red-700"
      : accent === "amber" ? "text-amber-700"
      : "text-slate-900";
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-2 py-2">
      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 text-lg font-extrabold leading-none ${valueClass}`}>{value}</div>
    </div>
  );
}

function Callout({ tone, children }: { tone: "amber" | "emerald" | "red" | "slate"; children: React.ReactNode }) {
  const toneCls =
    tone === "amber"   ? "bg-amber-50 border-amber-200 text-amber-900"
    : tone === "emerald" ? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : tone === "red"   ? "bg-red-50 border-red-200 text-red-900"
    : "bg-slate-50 border-slate-200 text-slate-700";
  return <div className={`rounded-md border px-3 py-2 text-[12px] ${toneCls}`}>{children}</div>;
}

// ────────────────────────────────────────────────────────────────────────
// AuditDetailContent — per-rep shifts table + coachable flags.
// Filtered version of the v1 ShiftActivityCard, scoped to the selected rep.
// ────────────────────────────────────────────────────────────────────────
function AuditDetailContent({ loading, shifts }: { loading: boolean; shifts: ShiftRow[] }) {
  const flagSummary = useMemo(() => {
    let lowContact = 0, lowAppt = 0, shortSess = 0, fewPings = 0;
    for (const s of shifts) {
      if (s.has_low_contact)   lowContact++;
      if (s.has_low_appt)      lowAppt++;
      if (s.has_short_session) shortSess++;
      if (s.has_few_pings)     fewPings++;
    }
    return { lowContact, lowAppt, shortSess, fewPings };
  }, [shifts]);

  if (loading) {
    return <div className="py-4 text-center text-xs text-slate-500">Loading shifts…</div>;
  }
  if (shifts.length === 0) {
    return <div className="rounded-md border border-dashed border-slate-300 p-4 text-center text-xs text-slate-500">No shifts logged in the last 30 days.</div>;
  }

  return (
    <>
      {/* Flag chips */}
      <div className="flex flex-wrap gap-1.5">
        {flagSummary.shortSess > 0 ? (
          <FlagChip tone="red">⚠ Short session · {flagSummary.shortSess}</FlagChip>
        ) : null}
        {flagSummary.lowContact > 0 ? (
          <FlagChip tone="amber">⚠ Low K→C · {flagSummary.lowContact}</FlagChip>
        ) : null}
        {flagSummary.lowAppt > 0 ? (
          <FlagChip tone="amber">⚠ Low C→A · {flagSummary.lowAppt}</FlagChip>
        ) : null}
        {flagSummary.fewPings > 0 ? (
          <FlagChip tone="slate">⚠ Few pings · {flagSummary.fewPings}</FlagChip>
        ) : null}
        {flagSummary.lowContact + flagSummary.lowAppt + flagSummary.shortSess + flagSummary.fewPings === 0 ? (
          <FlagChip tone="emerald">✓ No coachable flags</FlagChip>
        ) : null}
      </div>

      {/* Shifts table */}
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="text-[9px] uppercase tracking-wider text-slate-500">
            <tr className="border-b border-slate-200">
              <th className="py-1.5 pr-2 text-left">Day</th>
              <th className="px-2 text-right">Hrs</th>
              <th className="px-2 text-right">K</th>
              <th className="px-2 text-right">C</th>
              <th className="px-2 text-right">A</th>
              <th className="px-2 text-right">Cl</th>
              <th className="px-2 text-right">Mi</th>
              <th className="px-2 text-right">Pings</th>
              <th className="px-2 text-left">Flags</th>
            </tr>
          </thead>
          <tbody>
            {shifts.slice(0, 16).map((s) => {
              const flagged = s.has_low_contact || s.has_low_appt || s.has_short_session || s.has_few_pings;
              return (
                <tr key={s.shift_id} className={`border-b border-slate-100 ${flagged ? "bg-orange-50/40" : ""}`}>
                  <td className="py-1.5 pr-2 font-bold text-slate-700">
                    {new Date(s.started_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    <span className="ml-1 text-[9px] font-normal text-slate-500">
                      {new Date(s.started_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                    </span>
                  </td>
                  <td className="px-2 text-right">{Number(s.hours_worked).toFixed(1)}</td>
                  <td className="px-2 text-right">{s.knocks}</td>
                  <td className="px-2 text-right">{s.contacts}</td>
                  <td className="px-2 text-right">{s.appts}</td>
                  <td className="px-2 text-right">{s.claims}</td>
                  <td className="px-2 text-right">{s.miles_walked != null ? Number(s.miles_walked).toFixed(1) : "—"}</td>
                  <td className="px-2 text-right text-slate-500">{s.pings}</td>
                  <td className="px-2 text-left">
                    <span className="inline-flex flex-wrap gap-1">
                      {s.has_short_session ? <FlagChip tone="red" small>⚠ short</FlagChip> : null}
                      {s.has_low_contact   ? <FlagChip tone="amber" small>⚠ K→C</FlagChip> : null}
                      {s.has_low_appt      ? <FlagChip tone="amber" small>⚠ C→A</FlagChip> : null}
                      {s.has_few_pings     ? <FlagChip tone="slate" small>⚠ pings</FlagChip> : null}
                      {!flagged ? <span className="text-slate-300">—</span> : null}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {shifts.length > 16 ? (
          <div className="mt-2 text-[10px] text-slate-500">Showing first 16 of {shifts.length} shifts in window.</div>
        ) : null}
      </div>
    </>
  );
}

function FlagChip({ tone, small, children }: { tone: "amber" | "red" | "slate" | "emerald"; small?: boolean; children: React.ReactNode }) {
  const toneCls =
    tone === "amber"   ? "bg-amber-100 text-amber-800"
    : tone === "red"   ? "bg-red-100 text-red-800"
    : tone === "emerald" ? "bg-emerald-100 text-emerald-700"
    : "bg-slate-200 text-slate-700";
  const sizeCls = small ? "px-1 py-0.5 text-[9px]" : "px-1.5 py-0.5 text-[10px]";
  return <span className={`inline-flex items-center rounded font-bold ${toneCls} ${sizeCls}`}>{children}</span>;
}
